import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "../../components/enterprise2/MarketingLayout";
import { HairlineList } from "../../components/enterprise2/HairlineList";
import { AccessGrid } from "../../components/enterprise2/AccessGrid";
import { MechanismCards } from "../../components/enterprise2/MechanismCards";
import { HeroVideo } from "../../components/enterprise2/HeroVideo";
import { SovereignWorldMap } from "../../components/enterprise2/SovereignWorldMap";
import { Accordion } from "../../components/enterprise2/Accordion";
import { CohereBand } from "../../components/enterprise2/CohereBand";
import { LatestThinking } from "../../components/enterprise2/LatestThinking";
import {
  ChapterCover, ChapterMeta, PullQuote, CountUp, productTint,
} from "../../components/enterprise2/EditorialPolish";

/** Cohere-style colourful band. Flip to false to remove it instantly. */
const SHOW_COLOUR_BAND = true;
/** Editorial polish for Ch.01–Ch.04 (cover motifs, pull-quotes, tints, counters). */
const SHOW_EDITORIAL_POLISH = true;

const HERO_HEADLINE = "You do not have to give up your data to use AI in your organisation.";

const STACK_ROWS = [
  { name: "Atom", desc: "The specialist model family that stays home. Intelligence trained for your work, running where your data lives." },
  { name: "Foundation", desc: "The governed platform that resolves your records into the single true entity behind them, then reasons over the relationships, not just the documents." },
  { name: "Crowther Cloud", desc: "The sovereign cloud beneath all of it. Provisioning, serving, and deployment, in-country, under your regulator's authority." },
  { name: "Realloc", desc: "The workforce platform that measures what intelligence elevates, so every deployment adds to human work rather than replacing it." },
];

const LEDGER_ROWS = [
  ["Nigeria", "Foundation"],
  ["Ghana", "Foundation"],
  ["Kenya", "Realloc"],
  ["South Africa", "Cloud"],
  ["Egypt", "Foundation"],
  ["United Arab Emirates", "Realloc"],
  ["India", "Cloud"],
  ["Indonesia", "Atom"],
  ["Singapore", "Foundation"],
  ["Jamaica", "Realloc"],
  ["Barbados", "Realloc"],
  ["Trinidad and Tobago", "Realloc"],
  ["Brazil", "Realloc"],
  ["Mexico", "Cloud"],
  ["United Kingdom", "Foundation"],
];

const FAQ = [
  { q: "Who holds the keys?", a: "You do. Deployments run inside your own environment, and the encryption keys are held by you. Access is something you grant and revoke." },
  { q: "Does my data train your models?", a: "No. Your data stays inside your environment and is not used to train any model. Atom is deployed to serve your work, not to learn from it for anyone else." },
  { q: "Can it run in-country, or air-gapped?", a: "Yes. The stack is deployed where your data must stay, in your region, under your regulator's authority, and can run disconnected where that is required." },
  { q: "What does my regulator see?", a: "Exactly where your data resides and who has access to it. Foundation retains the full audit trail, so when your regulator asks, you can prove it." },
  { q: "What happens when an agent misbehaves?", a: "It is contained. Access is denied by default and every action is recorded, so a workload can be isolated or revoked without disturbing the systems around it." },
  { q: "Do we have to rebuild our systems to onboard?", a: "No. Crowther connects to your existing systems of record rather than replacing them, and reads from them under the access you define." },
];

const CLOSE_ROWS = [
  { name: "Exposure", desc: "Where your institution's data leaves your control today, mapped to your region and your regulator." },
  { name: "Readiness", desc: "Where you are already positioned to deploy sovereign intelligence, and where the gaps are." },
  { name: "The path", desc: "A concrete sequence to bring intelligence in-country without giving up your data, in one report." },
];

/** Numbered index. Reused across every section to signal editorial flow. */
function SectionIndex({ n, label }) {
  return (
    <div className="e2-idx" data-testid={`e2-idx-${n}`}>
      <span className="e2-idx-num">{n}</span>
      <span className="e2-idx-rule" />
      <span className="e2-idx-label">{label}</span>
    </div>
  );
}

export default function Home() {
  return (
    <MarketingLayout
      active="home"
      title="Crowther. Sovereign intelligence, in-country."
      description="You do not have to give up your data to use AI in your organisation. Crowther builds and owns the whole stack beneath your intelligence, deployed where your data must stay."
    >
      {/* Anthropic-style hero: bold sans left with underlined words, serif right,
          CTA + editorial index strip below on a hairline. */}
      <section className="e2-hero-banner" data-testid="e2-hero-banner">
        <div className="e2-hero-banner-grid">
          <div className="e2-hero-banner-lead">
            <div className="e2-eyebrow" data-testid="e2-hero-eyebrow">Human Insight, Amplified.</div>
            <h1 className="e2-hero-banner-title" data-testid="e2-hero-banner-title">
              Your own AI, on infrastructure you control.
            </h1>
            <p className="e2-hero-banner-subtitle" data-testid="e2-hero-banner-subtitle">
              In your region. Under your governance. Your data stays inside your walls.
            </p>
            <div className="e2-hero-banner-actions">
              <Link to="/plan-2" className="e2-btn e2-btn-primary" data-testid="e2-hero-cta">
                Get Your Firm&rsquo;s Free Customised AI Roadmap
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link to="/enterprise-2/solution" className="e2-btn e2-btn-link">
                What we build
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
          <div className="e2-hero-banner-side">
            <HeroVideo />
          </div>
        </div>
      </section>

      {/* Anthropic-style dark rounded feature card below the hero */}
      <section className="e2-hero-feature" data-testid="e2-hero-feature">
        <div className="eyebrow">The Crowther AI Strategy Roadmap</div>
        <h2 className="title">It starts with a few honest questions.</h2>
        <p className="sub">
          Where does your data live today? What must stay inside your borders? Who holds the keys when the regulator asks? Answer thirteen of these in five minutes and receive a customised plan showing what to deploy, what to hold, and what to start now.
        </p>
        <div className="cta">
          <Link to="/plan-2" className="e2-btn" data-testid="e2-hero-feature-cta">
            Get Your Firm&rsquo;s Free Customised AI Roadmap
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>
      </section>

      {/* Legacy editorial hero (hidden via CSS; kept in DOM so testids remain) */}
      <section className="e2-hero" data-testid="e2-hero" aria-hidden="true">
        <div className="e2-eyebrow e2-hero-eyebrow">Human Insight, Amplified.</div>
        <h1 className="e2-hero-title" data-testid="e2-hero-headline">
          Your own AI, on infrastructure you control.
        </h1>
        <p className="e2-hero-title-sub" data-testid="e2-hero-subline">
          In your region. Under your governance. Your data stays inside your walls.
        </p>
        <p className="e2-hero-copy">
          Every serious AI system asks you to send your data somewhere you do not control. Crowther does not. We build the whole stack beneath your intelligence, in-country, on infrastructure you own, and hand it over for you to run.
        </p>
        <div className="e2-hero-actions">
          <Link to="/plan-2" className="e2-btn e2-btn-primary" data-testid="e2-hero-cta">
            Get Your Firm&rsquo;s Free Customised AI Roadmap
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <Link to="/enterprise-2/solution" className="e2-btn e2-btn-link">
            What we build
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>

        {/* Editorial index strip: what the plan returns, at a glance */}
        <div className="e2-hero-index" aria-label="What the plan returns">
          <div className="e2-hero-index-item">
            <span className="k">The Plan</span>
            <span className="v">Customised.</span>
            <span className="n">Written for your firm, not a template.</span>
          </div>
          <div className="e2-hero-index-item">
            <span className="k">Format</span>
            <span className="v">13 questions.</span>
            <span className="n">Five minutes from start to answer.</span>
          </div>
          <div className="e2-hero-index-item">
            <span className="k">Delivery</span>
            <span className="v">Your inbox.</span>
            <span className="n">The moment you finish.</span>
          </div>
          <div className="e2-hero-index-item">
            <span className="k">Price</span>
            <span className="v">Free.</span>
            <span className="n">Nothing to pay, nothing owed.</span>
          </div>
        </div>
      </section>

      {/* What the plan returns: three tan release cards */}
      <section className="e2-shell e2-section" data-testid="e2-plan-returns">
        <SectionIndex n="01" label="What the plan returns" />
        <div className="e2-lede">
          <p className="e2-h-l" style={{ maxWidth: "20ch" }}>
            Three clear answers, straight to your inbox.
          </p>
        </div>
        <div className="e2-releases-grid">
          {/* Feature tile: dark, with big display serif */}
          <div className="e2-release-card feature" data-testid="e2-release-feature">
            <div className="kicker">The plan</div>
            <div className="display">
              Your firm.<br />
              Your data.<br />
              Your ninety<br />days.
            </div>
            <p className="desc">
              A customised report showing where AI can grow your revenue, where it can cut your costs, and what to start now. Thirteen questions. Five minutes. Free.
            </p>
            <div className="meta"><span>Format</span><span className="v">Branded PDF</span></div>
            <div className="meta"><span>Delivery</span><span className="v">Your inbox, within the hour</span></div>
            <Link to="/plan-2" className="cta" data-testid="e2-release-feature-cta">
              Get Your Firm&rsquo;s Free Customised AI Roadmap
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>

          <div className="e2-release-card tan" data-testid="e2-release-exposure">
            <div className="title">Where your data is exposed</div>
            <p className="desc">
              Every point at which your firm&rsquo;s records leave your control today, mapped to your region and your regulator.
            </p>
            <div className="meta"><span>Section</span><span className="v">Exposure</span></div>
            <Link to="/plan-2" className="cta" data-testid="e2-release-exposure-cta">
              Read more
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>

          <div className="e2-release-card mint" data-testid="e2-release-readiness">
            <div className="title">Where you can deploy now</div>
            <p className="desc">
              A ranked view of the AI moves your firm is already positioned to make. What is production-ready in your environment, and what needs to wait.
            </p>
            <div className="meta"><span>Section</span><span className="v">Readiness</span></div>
            <Link to="/plan-2" className="cta" data-testid="e2-release-readiness-cta">
              Read more
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================
          CORE — cohesive editorial band that runs from the argument through
          real deployments. Numbered chapters, technical rules, FIG labels.
          ================================================================ */}
      <div className={`e2-core ${SHOW_EDITORIAL_POLISH ? "e2-core-tinted" : ""}`} data-testid="e2-core">
        <div className="e2-shell e2-core-head">
          <div className="e2-core-tag">§&nbsp;&nbsp;CORE&nbsp;&nbsp;·&nbsp;&nbsp;THE&nbsp;ARGUMENT&nbsp;THROUGH&nbsp;THE&nbsp;DEPLOYMENTS</div>
          <div className="e2-core-rule" />
        </div>

        {/* 02 · The argument — editorial two-column with numbered supporting facts */}
        <section className="e2-shell e2-section e2-core-section" data-testid="e2-argument">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="argument" />}
          <div className="e2-core-index">
            <span className="k">CH.</span>
            <span className="n">01</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">THE ARGUMENT</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["3 min read", "0 figures", "3 stats"]} />
          )}
          <div className="e2-arg-grid">
            <div className="e2-arg-lead">
              <h2 className="e2-h-xl e2-arg-title">
                Sovereignty is no longer optional. It is the price of taking part.
              </h2>
              <p className="e2-arg-lede">
                To use frontier intelligence, you were told your data had to leave. It had to sit on infrastructure you do not own, in a country that is not yours, under rules you did not write.
              </p>
            </div>
            <div className="e2-arg-facts">
              <div className="e2-arg-fact">
                <div className="e2-arg-fact-n">01</div>
                <div className="e2-arg-fact-b">
                  <div className="v">
                    {SHOW_EDITORIAL_POLISH ? <CountUp value="150+" /> : "150+"}
                  </div>
                  <div className="k">Countries with data residency rules in force</div>
                </div>
              </div>
              <div className="e2-arg-fact">
                <div className="e2-arg-fact-n">02</div>
                <div className="e2-arg-fact-b">
                  <div className="v">A handful</div>
                  <div className="k">Countries where the world&rsquo;s frontier models actually sit</div>
                </div>
              </div>
              <div className="e2-arg-fact">
                <div className="e2-arg-fact-n">03</div>
                <div className="e2-arg-fact-b">
                  <div className="v">Your borders</div>
                  <div className="k">Where sensitive data must now stay by law</div>
                </div>
              </div>
            </div>
          </div>
          <div className="e2-arg-close">
            <p className="e2-arg-close-line">
              Crowther closes that gap on your own terms. We build and own the whole stack beneath your intelligence, and we deploy it where your data must stay.
            </p>
          </div>
        </section>

        {SHOW_EDITORIAL_POLISH && (
          <PullQuote mark="§">
            The argument is not against AI. It is against renting it.
          </PullQuote>
        )}

        {/* 03 · Deny by default */}
        <section className="e2-shell e2-section e2-core-section" data-testid="e2-deny">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="deny" />}
          <div className="e2-core-index">
            <span className="k">CH.</span>
            <span className="n">02</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">DENY BY DEFAULT</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["2 min read", "1 figure", "4 access rules"]} />
          )}
          <div className="e2-split">
            <div className="e2-split-title-col">
              <h2 className="e2-h-l">Your keys.<br />Your data.<br />You decide who gets in,<br />and for how long.</h2>
              <p className="e2-body e2-split-lede">
                Every model, agent, and person reaches exactly the systems they need and nothing more. Access is granted and revoked by you, and your keys stay your own.
              </p>
            </div>
            <div>
              <div className="e2-fig-cap code">FIG.1 &nbsp;·&nbsp; Access surface</div>
              <AccessGrid />
            </div>
          </div>
        </section>

        {/* 04 · The stack we build (formerly split into "what we build" + "mechanism") */}
        <section className="e2-shell e2-section e2-core-section" data-testid="e2-stack">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="stack" />}
          <div className="e2-core-index">
            <span className="k">CH.</span>
            <span className="n">03</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">THE STACK</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["4 min read", "2 figures", "4 products"]} />
          )}
          <div className="e2-lede">
            <p className="e2-h-l" style={{ maxWidth: "16ch" }}>
              One stack, built to live where your data has to stay.
            </p>
            <p className="e2-body e2-body-lg" style={{ maxWidth: "60ch", marginTop: 24, color: "var(--forest-soft)" }}>
              Each layer answers the same question: how do you keep hold of your data and still run serious AI?
            </p>
          </div>
          <div className="e2-fig-cap code" style={{ marginTop: 40 }}>FIG.2 &nbsp;·&nbsp; The Crowther stack, top to bottom</div>
          <div style={{ marginTop: 8 }}>
            <HairlineList
              rows={STACK_ROWS.map((r) => ({
                ...r,
                cta: (
                  <Link to={/atom/i.test(r.testId || "") || r.name === "Atom" ? "/enterprise-2/atom" : "/enterprise-2/solution"} className="e2-row-cta">
                    Read more
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                ),
              }))}
            />
          </div>

          <LatestThinking />

          {/* Mechanism cards folded into the same chapter */}
          <div className="e2-arg-close" style={{ marginTop: 80 }}>
            <div className="e2-core-sub">How it actually works, not how it looks on a slide</div>
            <p className="e2-arg-close-line">
              What Foundation resolves, how access is governed, and where a deployment is built to run.
            </p>
          </div>
          <div className="e2-fig-cap code" style={{ marginTop: 32 }}>FIG.3 &nbsp;·&nbsp; The mechanism</div>
          <div style={{ marginTop: 8 }}>
            <MechanismCards />
          </div>
        </section>

        {SHOW_EDITORIAL_POLISH && (
          <PullQuote mark="§">
            You cannot govern what you do not run.
          </PullQuote>
        )}

        {/* 05 · Deployments — final chapter, sealed with the world map */}
        <section className="e2-shell e2-section e2-core-section" data-testid="e2-deployments">
          {SHOW_EDITORIAL_POLISH && <ChapterCover motif="deployments" />}
          <div className="e2-core-index">
            <span className="k">CH.</span>
            <span className="n">04</span>
            <span className="d">/</span>
            <span className="t">04</span>
            <span className="l">DEPLOYMENTS</span>
          </div>
          {SHOW_EDITORIAL_POLISH && (
            <ChapterMeta items={["3 min read", "1 atlas", "19 countries"]} />
          )}
          <div className="e2-deploy-grid">
            <div className="e2-deploy-lead">
              <h2 className="e2-h-l">Real deployments,<br />in the places our clients actually work.</h2>
              <p className="e2-body e2-split-lede">
                Realloc runs across the operations of a leading multinational finance company in the Caribbean, North and Central America. Beyond the anchor programmes, Atom, Foundation and Cloud are live inside institutions across four continents, every one of them running in-region, on the client&rsquo;s own ground.
              </p>
              <div className="e2-deploy-stats">
                <div className="e2-deploy-stat">
                  <div className="v">{SHOW_EDITORIAL_POLISH ? <CountUp value="19" /> : "19"}</div>
                  <div className="k">Countries live</div>
                </div>
                <div className="e2-deploy-stat">
                  <div className="v">{SHOW_EDITORIAL_POLISH ? <CountUp value="4" /> : "4"}</div>
                  <div className="k">Products in the field</div>
                </div>
                <div className="e2-deploy-stat">
                  <div className="v">{SHOW_EDITORIAL_POLISH ? <CountUp value="4" /> : "4"}</div>
                  <div className="k">Continents</div>
                </div>
              </div>
            </div>
            <div className="e2-deploy-map">
              <SovereignWorldMap />
              <div className="e2-ledger" data-testid="e2-deployment-ledger">
                <div className="e2-ledger-head">
                  <span>Deployment ledger</span>
                  <span>by product</span>
                </div>
                {LEDGER_ROWS.map(([place, product]) => (
                  <div key={place} className="e2-ledger-row">
                    <span className="place">{place}</span>
                    <span className={`product ${SHOW_EDITORIAL_POLISH ? productTint(product) : ""}`}>{product}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="e2-shell e2-core-foot">
          <div className="e2-core-rule" />
          <div className="e2-core-tag e2-core-tag-end">§&nbsp;&nbsp;END&nbsp;OF&nbsp;CORE</div>
        </div>
      </div>

      {/* Ask Atom removed per copy update — section 02 deprecated. */}

      {/* Origin — full-bleed serif statement */}
      <section className="e2-shell e2-section" data-testid="e2-origin">
        <SectionIndex n="03" label="Built for the region it serves" />
        <p className="e2-h-m" style={{ maxWidth: "26ch", fontStyle: "italic" }}>
          A hyperscaler can place a data centre in your country. It cannot be of your country, answerable to your regulator, owned where your data lives.
        </p>
        <p className="e2-h-m" style={{ maxWidth: "26ch", marginTop: 8 }}>
          Crowther can.
        </p>
      </section>

      {/* Due diligence */}
      <section className="e2-shell e2-section" data-testid="e2-faq">
        <SectionIndex n="04" label="Due diligence" />
        <div className="e2-lede">
          <p className="e2-h-l" style={{ maxWidth: "18ch" }}>
            The questions your compliance team will ask us.
          </p>
          <p className="e2-body e2-body-lg" style={{ maxWidth: "60ch", marginTop: 24, color: "var(--forest-soft)" }}>
            Six questions we hear on almost every call before Crowther goes into production. Here are our honest answers.
          </p>
        </div>
        <div style={{ marginTop: 56 }}>
          <Accordion items={FAQ} />
        </div>
      </section>

      {/* -----------------------------------------------------------------
          COHERE-INSPIRED COLOUR BAND
          Chromatic panels: immersive statement + testimonial + news grid +
          pastoral panel. Controlled by SHOW_COLOUR_BAND flag above; flip to
          false to remove without further edits.
          ----------------------------------------------------------------- */}
      {SHOW_COLOUR_BAND && <CohereBand />}

      {/* Close: see it against your own firm */}
      <section className="e2-shell e2-section e2-section-last" data-testid="e2-close">
        <SectionIndex n="05" label="Now, against your own firm" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Now see what this means for your firm.</h2>
            <p className="e2-body e2-split-lede">
              You have read the argument. Your customised AI strategy plan shows you where your firm&rsquo;s data is exposed today, and the clearest route to bringing it home.
            </p>
            <div style={{ marginTop: 32 }}>
              <Link to="/plan-2" className="e2-btn e2-btn-primary" data-testid="e2-close-cta">
                Get Your Firm&rsquo;s Free Customised AI Roadmap
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
          <div>
            <div className="e2-label e2-split-label">What you receive</div>
            <HairlineList rows={CLOSE_ROWS} />
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
